import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { mediaUrl } from '../api/client';
import { fetchSettings } from '../api/services';
import { mergeFooter } from '../lib/footerDefaults';

const SettingsContext = createContext({ settings: {}, refresh: () => {}, loading: true, ready: false });

/** Naikkan versi ini jika skema cache berubah — cache lama dibuang otomatis */
const CACHE_VERSION = 'v3';
const CACHE_KEY = 'belbel_settings_light';
const CACHE_VER_KEY = 'belbel_settings_ver';

const LIGHT_KEYS = [
  'brand_name', 'brand_full', 'whatsapp_number', 'whatsapp_number_alt',
  'instagram', 'shopee', 'tokopedia', 'email', 'address',
  'seo_title', 'seo_description', 'seo_keywords',
  'logo_header', 'logo_footer', 'favicon', 'navigation',
  'updated_at',
];

function clearStaleCaches() {
  try {
    const ver = localStorage.getItem(CACHE_VER_KEY);
    if (ver !== CACHE_VERSION) {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem('belbel_settings');
      localStorage.removeItem('belbel_logo_header');
      localStorage.removeItem('belbel_logo_footer');
      localStorage.setItem(CACHE_VER_KEY, CACHE_VERSION);
    }
  } catch {
    // ignore
  }
}

function readCache() {
  clearStaleCaches();
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return {};
}

function writeCache(data) {
  try {
    const light = {};
    LIGHT_KEYS.forEach((k) => {
      if (data[k] === undefined || data[k] === null) return;
      if (typeof data[k] === 'string' && data[k].startsWith('data:') && data[k].length > 8000) return;
      light[k] = data[k];
    });
    localStorage.setItem(CACHE_KEY, JSON.stringify(light));
    localStorage.setItem(CACHE_VER_KEY, CACHE_VERSION);
    localStorage.removeItem('belbel_settings');
    // Logo cache hanya untuk offline fallback, bukan first paint
    if (light.logo_header) localStorage.setItem('belbel_logo_header', light.logo_header);
    if (light.logo_footer) localStorage.setItem('belbel_logo_footer', light.logo_footer);
  } catch {
    try {
      if (data.logo_header && !String(data.logo_header).startsWith('data:')) {
        localStorage.setItem('belbel_logo_header', data.logo_header);
      }
      if (data.logo_footer && !String(data.logo_footer).startsWith('data:')) {
        localStorage.setItem('belbel_logo_footer', data.logo_footer);
      }
    } catch {
      // ignore
    }
  }
}

function normalizeSettings(data) {
  const next = { ...data };
  const pb = next.page_builder && typeof next.page_builder === 'object' ? next.page_builder : {};
  next.page_builder = { ...pb, footer: mergeFooter(pb.footer) };
  if (next.logo_header) next.logo_header = mediaUrl(next.logo_header);
  if (next.logo_footer) next.logo_footer = mediaUrl(next.logo_footer);
  if (next.favicon) next.favicon = mediaUrl(next.favicon);
  return next;
}

export function SettingsProvider({ children }) {
  // Jangan hydrate dari cache → hindari flash data lama saat refresh
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const applySettings = useCallback((data) => {
    const normalized = normalizeSettings(data);
    setSettings(normalized);
    writeCache(normalized);
    if (normalized.favicon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = `${normalized.favicon}${normalized.favicon.includes('?') ? '&' : '?'}v=${encodeURIComponent(normalized.updated_at || Date.now())}`;
    }
    if (normalized.seo_title) document.title = normalized.seo_title;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSettings({ _t: Date.now() });
      applySettings(data);
      setReady(true);
    } catch {
      // Offline: baru pakai cache sebagai fallback
      const cached = readCache();
      if (cached && Object.keys(cached).length) {
        applySettings(cached);
        setReady(true);
      }
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    clearStaleCaches();
    refresh();
  }, [refresh]);

  return (
    <SettingsContext.Provider value={{ settings, refresh, loading, ready }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}

/** Logo: tunggu API dulu — jangan tampilkan logo cache lama */
export function useLogoHeader() {
  const { settings, loading, ready } = useSettings();
  if (loading && !ready) {
    return { logo: '', loading: true };
  }
  const logo = mediaUrl(settings.logo_header || '');
  return { logo, loading: false };
}

export function useLogoFooter() {
  const { settings, loading, ready } = useSettings();
  if (loading && !ready) {
    return { logo: '', loading: true };
  }
  const logo = mediaUrl(settings.logo_footer || '');
  return { logo, loading: false };
}
